import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2, MailCheck, PlugZap, Save, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { AuthRequiredState } from '@/components/AuthRequiredState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  CustomerEditForm,
  type CustomerFormValues,
  buildCustomerEditInitialState,
  buildUpdatePayload,
  createCustomerFormSchema,
  customerDialogCopy,
} from '@/components/CustomerEditDialog';
import { MailSyncTraceDialog } from '@/components/admin/MailSyncTraceDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import {
  canRunCustomerMailboxSync,
  useArchiveCustomer,
  useCustomerAdminDetail,
  useTestCustomerMailboxSync,
  useTestCustomerSapConnection,
  useUpdateCustomer,
} from '@/lib/queries/customersAdmin';

export const Route = createFileRoute('/admin/tenants/$id')({
  component: AdminTenantDetailRoute,
});

const FEEDBACK_ERROR_CLASS = 'rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive';
const FEEDBACK_SUCCESS_CLASS =
  'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800';
const SUCCESS_FEEDBACK_TIMEOUT_MS = 5000;

function AdminTenantDetailRoute() {
  const { id } = Route.useParams();
  return <AdminTenantDetailPage tenantId={id} />;
}

export function AdminTenantDetailPage({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate({ from: '/admin/tenants/$id' });
  const { language } = useI18n();
  const formCopy = customerDialogCopy(language);
  const copy =
    language === 'en'
      ? {
          sectionLabel: 'Admin',
          back: 'Back to admin',
          loading: 'Loading tenant…',
          forbidden: 'This view is restricted to admins.',
          notFoundTitle: 'Tenant not found',
          notFoundDescription: 'The requested tenant no longer exists or is no longer visible.',
          overviewTitle: 'Tenant overview',
          overviewDescription: 'Configuration and operational summary for this tenant.',
          delete: 'Delete tenant',
          testMailSync: 'Test email sync',
          testMailSyncUnavailable:
            'Mail scan is incomplete. Connect the mailbox OAuth flow or provide the password first.',
          testMailSyncError: 'Email sync test failed',
          testMailSyncDone: (
            messagesSeen: number,
            attachmentsProcessed: number,
            attachmentsSkipped: number,
          ) =>
            `Email sync test finished: ${messagesSeen} messages, ${attachmentsProcessed} attachments imported, ${attachmentsSkipped} skipped.`,
          testSapConnection: 'Test SAP connection',
          testSapConnectionUnavailable: 'SAP is not configured for this tenant.',
          testSapConnectionError: 'SAP connection test failed',
          testSapConnectionDone: (system: string, status?: number) =>
            `SAP connection verified: ${system}${status ? ` (${status})` : ''}.`,
          saveDone: 'Tenant settings saved.',
          saveError: 'Saving tenant settings failed',
          deleteTitle: 'Delete tenant?',
          deleteDescription: (name: string) => `Delete tenant ${name}?`,
        }
      : {
          sectionLabel: 'Admin',
          back: 'Zurück zur Admin-Übersicht',
          loading: 'Tenant wird geladen…',
          forbidden: 'Diese Ansicht ist nur für Admins freigegeben.',
          notFoundTitle: 'Tenant nicht gefunden',
          notFoundDescription:
            'Der angeforderte Tenant existiert nicht mehr oder ist nicht mehr sichtbar.',
          overviewTitle: 'Tenant-Übersicht',
          overviewDescription: 'Konfiguration und betriebliche Kennzahlen für diesen Tenant.',
          delete: 'Tenant löschen',
          testMailSync: 'E-Mail-Sync testen',
          testMailSyncUnavailable:
            'Mail-Scan ist unvollständig. Verbinde zuerst den Mailbox-OAuth-Flow oder hinterlege das Passwort.',
          testMailSyncError: 'E-Mail-Sync-Test fehlgeschlagen',
          testMailSyncDone: (
            messagesSeen: number,
            attachmentsProcessed: number,
            attachmentsSkipped: number,
          ) =>
            `E-Mail-Sync-Test abgeschlossen: ${messagesSeen} Nachrichten, ${attachmentsProcessed} Anhänge importiert, ${attachmentsSkipped} übersprungen.`,
          testSapConnection: 'SAP-Verbindung prüfen',
          testSapConnectionUnavailable: 'SAP ist für diesen Tenant nicht konfiguriert.',
          testSapConnectionError: 'SAP-Verbindungstest fehlgeschlagen',
          testSapConnectionDone: (system: string, status?: number) =>
            `SAP-Verbindung geprüft: ${system}${status ? ` (${status})` : ''}.`,
          saveDone: 'Tenant-Einstellungen gespeichert.',
          saveError: 'Speichern der Tenant-Einstellungen fehlgeschlagen',
          deleteTitle: 'Tenant löschen?',
          deleteDescription: (name: string) => `Tenant ${name} wirklich löschen?`,
        };

  const query = useCustomerAdminDetail(tenantId);
  const archiveMutation = useArchiveCustomer();
  const testMailSyncMutation = useTestCustomerMailboxSync();
  const testSapConnectionMutation = useTestCustomerSapConnection();
  const updateMutation = useUpdateCustomer();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mailSyncFeedback, setMailSyncFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const [mailSyncTraceOpen, setMailSyncTraceOpen] = useState(false);
  const [mailSyncTraceResult, setMailSyncTraceResult] = useState<Awaited<
    ReturnType<typeof testMailSyncMutation.mutateAsync>
  > | null>(null);
  const [sapConnectionFeedback, setSapConnectionFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const accessError = query.error instanceof ApiError ? query.error : null;
  const isUnauthorized = accessError?.status === 401;
  const isForbidden = accessError?.status === 403 || accessError?.code === 'forbidden';

  const tenant = query.data ?? null;
  const initialState = useMemo(() => buildCustomerEditInitialState(tenant ?? undefined), [tenant]);
  const formSchema = useMemo(() => createCustomerFormSchema(formCopy), [formCopy]);
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialState.defaults,
    mode: 'onBlur',
  });
  const titleId = useId();
  const nameId = useId();
  const contactNameId = useId();
  const contactEmailId = useId();
  const sapSystemId = useId();
  const sapBaseUrlId = useId();
  const sapClientId = useId();
  const sapCompanyCodeId = useId();
  const sapUsernameId = useId();
  const sapPasswordId = useId();
  const sapNangoProviderConfigKeyId = useId();
  const sapNangoConnectionId = useId();
  const sapNangoSecretId = useId();
  const mailboxEnabledId = useId();
  const mailScanEmailId = useId();
  const mailImapHostId = useId();
  const mailImapPortId = useId();
  const mailImapUsernameId = useId();
  const mailImapPasswordId = useId();
  const mailOauthTenantId = useId();
  const mailOauthClientId = useId();
  const mailGoogleOauthClientId = useId();
  const mailGoogleOauthClientSecretId = useId();
  const mailImapMailboxId = useId();

  useEffect(() => {
    form.reset(initialState.defaults);
  }, [form, initialState.defaults]);

  useEffect(() => {
    if (mailSyncFeedback?.kind !== 'success') return undefined;
    const timeoutId = window.setTimeout(
      () => setMailSyncFeedback(null),
      SUCCESS_FEEDBACK_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [mailSyncFeedback]);

  useEffect(() => {
    if (sapConnectionFeedback?.kind !== 'success') return undefined;
    const timeoutId = window.setTimeout(
      () => setSapConnectionFeedback(null),
      SUCCESS_FEEDBACK_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [sapConnectionFeedback]);

  useEffect(() => {
    if (saveFeedback?.kind !== 'success') return undefined;
    const timeoutId = window.setTimeout(() => setSaveFeedback(null), SUCCESS_FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [saveFeedback]);

  const submitting = updateMutation.isPending;

  const handleSubmit: SubmitHandler<CustomerFormValues> = async (values) => {
    if (!tenant) return;
    if (
      values.sapSystem !== 'none' &&
      values.sapAuthMethod === 'basic' &&
      !values.sapPassword &&
      !initialState.initialHasSapPassword
    ) {
      form.setError('sapPassword', {
        type: 'manual',
        message: formCopy.sapPasswordRequired,
      });
      return;
    }
    if (
      values.sapSystem !== 'none' &&
      values.sapAuthMethod === 'oauth2-client-credentials' &&
      !values.sapOauthClientSecret &&
      !initialState.initialHasSapOauthClientSecret
    ) {
      form.setError('sapOauthClientSecret', {
        type: 'manual',
        message: formCopy.sapOauthClientSecretRequired,
      });
      return;
    }
    if (
      values.sapSystem !== 'none' &&
      values.sapAuthMethod === 'nango-proxy' &&
      !values.sapNangoSecret &&
      !initialState.initialHasSapNangoSecret
    ) {
      form.setError('sapNangoSecret', {
        type: 'manual',
        message: formCopy.sapNangoSecretRequired,
      });
      return;
    }
    if (
      values.mailboxEnabled &&
      values.mailAuthMethod === 'gmail-api-oauth' &&
      !values.mailGoogleOauthClientSecret &&
      !initialState.initialHasGoogleOauthClientSecret
    ) {
      form.setError('mailGoogleOauthClientSecret', {
        type: 'manual',
        message: formCopy.googleClientSecretRequired,
      });
      return;
    }
    setSaveFeedback(null);
    try {
      const result = await updateMutation.mutateAsync({
        id: tenant.id,
        patch: buildUpdatePayload(values),
      });
      form.reset(buildCustomerEditInitialState(result).defaults);
      setSaveFeedback({ kind: 'success', message: copy.saveDone });
    } catch (error) {
      setSaveFeedback({
        kind: 'error',
        message: error instanceof Error ? error.message : copy.saveError,
      });
      throw error;
    }
  };

  async function handleTestMailSync() {
    if (!tenant) return;
    setMailSyncFeedback(null);
    try {
      const result = await testMailSyncMutation.mutateAsync(tenant.id);
      setMailSyncFeedback({
        kind: 'success',
        message: copy.testMailSyncDone(
          result.messagesSeen,
          result.attachmentsProcessed,
          result.attachmentsSkipped,
        ),
      });
      setMailSyncTraceResult(result);
      setMailSyncTraceOpen(true);
    } catch (error) {
      setMailSyncFeedback({
        kind: 'error',
        message: error instanceof Error ? error.message : copy.testMailSyncError,
      });
    }
  }

  async function handleTestSapConnection() {
    if (!tenant) return;
    setSapConnectionFeedback(null);
    try {
      const result = await testSapConnectionMutation.mutateAsync(tenant.id);
      setSapConnectionFeedback({
        kind: result.ok ? 'success' : 'error',
        message: result.ok
          ? copy.testSapConnectionDone(
              formatSapSystemLabel(result.system, result.authMethod),
              result.status,
            )
          : (result.message ?? copy.testSapConnectionError),
      });
    } catch (error) {
      setSapConnectionFeedback({
        kind: 'error',
        message: error instanceof Error ? error.message : copy.testSapConnectionError,
      });
    }
  }

  if (isUnauthorized) {
    return <AuthRequiredState message={accessError?.message} />;
  }

  if (isForbidden) {
    return (
      <Card className="border-amber-300/60 bg-amber-50/60">
        <CardContent className="py-10 text-sm text-muted-foreground">{copy.forbidden}</CardContent>
      </Card>
    );
  }

  if (query.isPending) {
    return (
      <Card>
        <CardContent className="py-10 text-sm text-muted-foreground">{copy.loading}</CardContent>
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <CardContent className="py-10 text-sm text-destructive">{query.error.message}</CardContent>
      </Card>
    );
  }

  if (!tenant) {
    return (
      <section className="flex flex-col gap-6">
        <Button
          type="button"
          variant="ghost"
          className="w-fit"
          onClick={() => {
            void navigate({ to: '/admin' });
          }}
        >
          <ArrowLeft className="size-4" aria-hidden />
          {copy.back}
        </Button>
        <Card>
          <CardHeader>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              {copy.sectionLabel}
            </p>
            <CardTitle>{copy.notFoundTitle}</CardTitle>
            <CardDescription>{copy.notFoundDescription}</CardDescription>
          </CardHeader>
        </Card>
      </section>
    );
  }

  const canTestMailSync = canRunCustomerMailboxSync(tenant);
  const canTestSapConnection = tenant.sapSystem !== 'none' && tenant.sapConfig !== undefined;

  return (
    <section className="flex flex-col gap-6">
      <Button
        type="button"
        variant="ghost"
        className="w-fit"
        onClick={() => {
          void navigate({ to: '/admin' });
        }}
      >
        <ArrowLeft className="size-4" aria-hidden />
        {copy.back}
      </Button>

      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {copy.sectionLabel}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{tenant.name}</h1>
          <p className="text-sm text-muted-foreground">{tenant.id}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="submit"
            form="customer-edit-form"
            disabled={submitting}
            data-testid="admin-tenant-detail-save"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Save className="size-4" aria-hidden />
            )}
            {formCopy.save}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={submitting || archiveMutation.isPending}
          >
            <Trash2 className="size-4" aria-hidden />
            {copy.delete}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void handleTestMailSync();
            }}
            disabled={!canTestMailSync || testMailSyncMutation.isPending || submitting}
            title={!canTestMailSync ? copy.testMailSyncUnavailable : undefined}
            data-testid={`admin-tenant-mail-sync-${tenant.id}`}
          >
            {testMailSyncMutation.isPending ? (
              <MailCheck className="size-4 animate-pulse" aria-hidden />
            ) : (
              <MailCheck className="size-4" aria-hidden />
            )}
            {copy.testMailSync}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void handleTestSapConnection();
            }}
            disabled={!canTestSapConnection || testSapConnectionMutation.isPending || submitting}
            title={!canTestSapConnection ? copy.testSapConnectionUnavailable : undefined}
            data-testid={`admin-tenant-sap-connection-${tenant.id}`}
          >
            {testSapConnectionMutation.isPending ? (
              <PlugZap className="size-4 animate-pulse" aria-hidden />
            ) : (
              <PlugZap className="size-4" aria-hidden />
            )}
            {copy.testSapConnection}
          </Button>
        </div>
      </header>

      {mailSyncFeedback ? (
        <output
          className={
            mailSyncFeedback.kind === 'error' ? FEEDBACK_ERROR_CLASS : FEEDBACK_SUCCESS_CLASS
          }
          data-testid="admin-tenant-mail-sync-feedback"
        >
          {mailSyncFeedback.message}
        </output>
      ) : null}

      {sapConnectionFeedback ? (
        <output
          className={
            sapConnectionFeedback.kind === 'error' ? FEEDBACK_ERROR_CLASS : FEEDBACK_SUCCESS_CLASS
          }
          data-testid="admin-tenant-sap-connection-feedback"
        >
          {sapConnectionFeedback.message}
        </output>
      ) : null}

      {saveFeedback ? (
        <output
          className={saveFeedback.kind === 'error' ? FEEDBACK_ERROR_CLASS : FEEDBACK_SUCCESS_CLASS}
          data-testid="admin-tenant-save-feedback"
        >
          {saveFeedback.message}
        </output>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle id={titleId}>{formCopy.editTitle}</CardTitle>
          <CardDescription>{formCopy.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <CustomerEditForm
            form={form}
            submitting={submitting}
            nameId={nameId}
            contactNameId={contactNameId}
            contactEmailId={contactEmailId}
            sapSystemId={sapSystemId}
            sapBaseUrlId={sapBaseUrlId}
            sapClientId={sapClientId}
            sapCompanyCodeId={sapCompanyCodeId}
            sapUsernameId={sapUsernameId}
            sapPasswordId={sapPasswordId}
            sapNangoProviderConfigKeyId={sapNangoProviderConfigKeyId}
            sapNangoConnectionId={sapNangoConnectionId}
            sapNangoSecretId={sapNangoSecretId}
            mailboxEnabledId={mailboxEnabledId}
            mailScanEmailId={mailScanEmailId}
            mailImapHostId={mailImapHostId}
            mailImapPortId={mailImapPortId}
            mailImapUsernameId={mailImapUsernameId}
            mailImapPasswordId={mailImapPasswordId}
            mailOauthTenantId={mailOauthTenantId}
            mailOauthClientId={mailOauthClientId}
            mailGoogleOauthClientId={mailGoogleOauthClientId}
            mailGoogleOauthClientSecretId={mailGoogleOauthClientSecretId}
            mailImapMailboxId={mailImapMailboxId}
            isEdit
            customerId={tenant.id}
            initialHasSapPassword={initialState.initialHasSapPassword}
            initialHasSapOauthClientSecret={initialState.initialHasSapOauthClientSecret}
            initialHasSapNangoSecret={initialState.initialHasSapNangoSecret}
            initialHasOauthRefreshToken={initialState.initialHasOauthRefreshToken}
            initialHasGoogleOauthClientSecret={initialState.initialHasGoogleOauthClientSecret}
            initialHasGoogleOauthRefreshToken={initialState.initialHasGoogleOauthRefreshToken}
            submitError={updateMutation.error}
            onSubmit={handleSubmit}
            open
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={copy.deleteTitle}
        description={copy.deleteDescription(tenant.name)}
        confirmLabel={copy.delete}
        cancelLabel={language === 'en' ? 'Cancel' : 'Abbrechen'}
        confirmVariant="destructive"
        pending={archiveMutation.isPending}
        onConfirm={() => {
          void archiveMutation.mutateAsync(tenant.id).then(() => {
            setConfirmOpen(false);
            void navigate({ to: '/admin' });
          });
        }}
        testIdPrefix="admin-tenant-detail-delete"
      />
      <MailSyncTraceDialog
        open={mailSyncTraceOpen}
        result={mailSyncTraceResult}
        onOpenChange={setMailSyncTraceOpen}
      />
    </section>
  );
}

function formatSapSystemLabel(system: 'sap-s4hana' | 'sap-erp-6', authMethod?: string) {
  const authLabel =
    authMethod === 'basic' ? 'Basic' : authMethod === 'nango-proxy' ? 'Nango' : 'OAuth2';
  return system === 'sap-s4hana' ? `SAP S/4HANA / ${authLabel}` : `SAP ERP 6.0 / ${authLabel}`;
}
